/**
 * Pure normalizer for the `GET /api/reports/enrollment-funnel/:aba` payload.
 *
 * The server is the single source of card labels, definitions, tones, and
 * order — this module only reshapes, and it is the whole backward-compat
 * story: it accepts BOTH the current payload (`range` / `optedIn` /
 * per-card `definition`, 8 cards) and the legacy one (`period: '3m'`, 9 cards
 * including `active`, no definitions), so admin and service can deploy in
 * either order.
 *
 * Card `count` semantics: `null`/`undefined` means "unavailable / not loaded";
 * a real zero is `0` and must survive normalization as `0`.
 *
 * The server's per-card `percent` / `direction` are NOT carried: `deltaLabel`
 * is the display string and `tone` is authoritative, both computed
 * server-side, so nothing here renders (or clamps) a percentage.
 */

const LEGACY_PERIOD_UNITS = Object.freeze({ d: 'day', w: 'week', m: 'month', y: 'year' });

/**
 * Caption label for a legacy server's rolling-window token: `'3m'` →
 * "Last 3 months", `'30d'` → "Last 30 days", `'mtd'` / `'ytd'` → "Month to
 * date" / "Year to date". Anything unrecognised falls back to the raw token
 * rather than inventing a meaning.
 *
 * @param {string} period
 * @returns {string}
 */
export function legacyPeriodLabel(period) {
    const token = String(period ?? '')
        .trim()
        .toLowerCase();
    if (token === 'mtd') return 'Month to date';
    if (token === 'ytd') return 'Year to date';
    const match = token.match(/^(\d+)([dwmy])$/);
    if (!match) return String(period);
    const count = Number(match[1]);
    return `Last ${count} ${LEGACY_PERIOD_UNITS[match[2]]}${count === 1 ? '' : 's'}`;
}

/**
 * @param {Object|null|undefined} payload
 * @returns {{
 *   range: { type: 'all_time'|'legacy_period', label: string, generatedAt: string|null } | null,
 *   optedIn: { count: number|null, definition: string } | null,
 *   cards: Array<{ key: string, label: string, count: number|null, deltaLabel: string,
 *                  tone: string, definition: string|null, variant: string, filterable: boolean }>
 * }}
 */
export function normalizeFunnelPayload(payload) {
    const rawCards = Array.isArray(payload?.funnel) ? payload.funnel : [];

    let range = null;
    if (payload?.range?.type === 'all_time' || payload?.period === 'all_time') {
        range = {
            type: 'all_time',
            label: 'All time',
            generatedAt: payload?.range?.generatedAt ?? null,
        };
    } else if (payload?.period) {
        // Legacy server: a fixed rolling window reported as a token ("3m").
        range = {
            type: 'legacy_period',
            label: legacyPeriodLabel(payload.period),
            generatedAt: null,
        };
    }

    const optedIn =
        payload?.optedIn && typeof payload.optedIn === 'object'
            ? {
                  count: payload.optedIn.count ?? null,
                  definition: payload.optedIn.definition ?? '',
              }
            : null;

    const cards = rawCards
        // Legacy payloads carry an `active` card that duplicated First Advance;
        // the server no longer sends it. Filter so the grid is always 8 cards
        // regardless of deploy order.
        .filter((card) => card?.key !== 'active')
        .map((card) => ({
            key: card.key,
            label: card.label,
            count: card.count == null ? null : card.count,
            deltaLabel: card.deltaLabel ?? '',
            tone: card.tone ?? 'neutral',
            definition: card.definition ?? null,
            variant: card.variant ?? 'default',
            // Per-caller: can the users list be filtered to this card's
            // population? Absent (legacy server) reads as "no" — a static tile.
            filterable: card.filterable === true,
        }));

    return { range, optedIn, cards };
}

/**
 * Label-free loading skeleton — 8 slots following the funnel's variant ramp
 * (white → blue-50 → blue-100 → blue-200 → primary). Deliberately carries no
 * labels or tones so placeholder copy can never drift from the server payload.
 */
export const FUNNEL_SKELETON = Object.freeze([
    { key: 'sk_1', variant: 'default' },
    { key: 'sk_2', variant: 'surface_50' },
    { key: 'sk_3', variant: 'surface_50' },
    { key: 'sk_4', variant: 'surface_50' },
    { key: 'sk_5', variant: 'surface_50' },
    { key: 'sk_6', variant: 'surface_100' },
    { key: 'sk_7', variant: 'surface_100' },
    { key: 'sk_8', variant: 'primary' },
]);
